// components/Filters/SearchableStringListDropdown.tsx
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import { useDropdownPortal } from '../../hooks/useDropdownPortal';

import ChevronDown from '../Icons/Chevron/ChevronDown';


/**
 * setup types for use with the component
 */
export type SearchableStringListDropdownHandle = {
    clear: () => void;
    setValue: (v: string | undefined, opts?: { fireOnChange?: boolean }) => void;
    getValue: () => string | undefined;
    open: () => void;
    close: () => void;
    focus: () => void; // focuses input if open, otherwise trigger button
};

type SearchableStringListDropdownProps = {
    options: ReadonlyArray<string>;
    defaultValue?: string;
    onChange?: (v: string | undefined) => void;

    label?: string;
    placeholder?: string;                  // button text when empty
    inputPlaceholder?: string;             // search box placeholder
    noResultsText?: string;
    clearable?: boolean;                   // default true

    size?: 'sm' | 'lg';
    disabled?: boolean;
    wrapperClassName?: string;
    triggerClassName?: string;
    buttonWidthCh?: number;                // fixed width to align with others

    disabledValues?: ReadonlyArray<string>;
    isValueDisabled?: (v: string) => boolean;
};

const SearchableStringListDropdownInner = (
    {
        options,
        defaultValue,
        onChange,
        label,
        placeholder = 'Select...',
        inputPlaceholder = 'Type to search…',
        noResultsText = 'No results',
        clearable = true,
        size,
        disabled,
        wrapperClassName,
        triggerClassName,
        buttonWidthCh,
        disabledValues,
        isValueDisabled
    }: SearchableStringListDropdownProps,
    ref: React.Ref<SearchableStringListDropdownHandle>
) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string | undefined>(defaultValue);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const sizeBtn = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';
    const { portal } = useDropdownPortal(containerRef, open, setOpen);

    // keep local selection in sync if defaultValue changes
    useEffect(() => { setSelected(defaultValue); }, [defaultValue]);

    const isDisabled = (v: string) =>
        (disabledValues?.includes(v) ?? false) || (isValueDisabled?.(v) ?? false);

    /**
     * Computes the filtered list of options based on the current search query.
     *
     * - Trims and lowercases the query for case-insensitive matching.
     * - If the query is empty, returns the full options list.
     * - Otherwise, returns only options that include the query substring.
     *
     * Memoized for performance; recalculates only when `options` or `query` changes.
     */
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options.slice();
        return options.filter(o => o.toLowerCase().includes(q));
    }, [options, query]);

    /**
     * Runs when the dropdown is opened.
     *
     * - Clears the search query.
     * - Sets the activeIndex to the currently selected item, or 0 if not found.
     * - Focuses the input field after render (via timeout to ensure DOM is ready).
     *
     * This ensures a smooth UX by resetting search and preparing keyboard nav.
     */
    useEffect(() => {
        if (!open) return;
        setQuery('');
        const idx = filtered.findIndex(o => selected !== undefined && o === selected);
        setActiveIndex(Math.max(0, idx));
        setTimeout(() => inputRef.current?.focus(), 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /**
     * Ensures the `activeIndex` remains valid if the filtered list shrinks.
     *
     * - If the current active index exceeds the new list length, it clamps it to the last valid index.
     * - Falls back to 0 if list is empty.
     *
     * This avoids indexing errors when navigating with Arrow keys or pressing Enter.
     */
    useEffect(() => {
        if (activeIndex >= filtered.length) {
            setActiveIndex(filtered.length > 0 ? filtered.length - 1 : 0);
        }
    }, [filtered, activeIndex]);

    const choose = (v: string) => {
        if (isDisabled(v)) return;
        setSelected(v);
        onChange?.(v);
        setOpen(false);
    };

    /**
     * Clears the selected value and closes the dropdown.
     *
     * Resets the internal `selected` state to `undefined`, triggers `onChange`
     * with `undefined` to notify the parent, and closes the dropdown menu.
     *
     * Wrapped in `useCallback` to ensure a stable reference for useImperativeHandle.
     */
    const clear = useCallback(() => {
        setSelected(undefined);
        onChange?.(undefined);
        setOpen(false);
    }, [onChange]);

    const onTriggerKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
        }
    };

    const scrollActiveIntoView = (index: number) => {
        const list = listRef.current;
        if (!list) return;
        const el = list.querySelector<HTMLButtonElement>(`[data-idx="${index}"]`);
        el?.scrollIntoView({ block: 'nearest' });
    };

    const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (!filtered.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            let i = activeIndex;
            for (let step = 0; step < filtered.length; step++) {
                i = (i + 1) % filtered.length;
                if (!isDisabled(filtered[i])) break;
            }
            setActiveIndex(i);
            scrollActiveIntoView(i);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            let i = activeIndex;
            for (let step = 0; step < filtered.length; step++) {
                i = (i - 1 + filtered.length) % filtered.length;
                if (!isDisabled(filtered[i])) break;
            }
            setActiveIndex(i);
            scrollActiveIntoView(i);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[activeIndex]) choose(filtered[activeIndex]);
        }
    };

    /**
     * Exposes imperative methods to the parent component through the `ref`.
     * 
     * These allow external components to programmatically interact with the dropdown:
     *
     * - `clear`: Resets the selected value and closes the dropdown.
     * - `setValue`: Sets a new selected value, and optionally triggers `onChange`.
     * - `getValue`: Returns the currently selected value.
     * - `open`: Opens the dropdown and focuses the input.
     * - `close`: Closes the dropdown.
     * - `focus`: Focuses the input if open; otherwise focuses the trigger button.
     *
     * This is useful when the parent needs direct control (e.g., form resets, coordinated UI flows).
     */
    useImperativeHandle(ref, () => ({
        clear: () => clear(),
        setValue: (v, opts) => {
            setSelected(v);
            if (opts?.fireOnChange) onChange?.(v);
        },
        getValue: () => selected,
        open: () => {
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
        },
        close: () => setOpen(false),
        focus: () => {
            if (open) inputRef.current?.focus();
            else buttonRef.current?.focus();
        }
    }), [selected, open, clear, onChange]);

    return (
        <div
            className={['position-relative', wrapperClassName].filter(Boolean).join(' ')}
            ref={containerRef}
        >
            {label && <label className="form-label fw-semibold d-block mb-1">{label}</label>}

            <button
                ref={buttonRef}
                type="button"
                className={[
                    'btn',
                    'btn-outline-secondary',
                    'btn-form-select',
                    'btn-form-focus',
                    'd-flex',
                    'align-items-center',
                    'justify-content-between',
                    'gap-2',
                    'text-truncate',
                    sizeBtn,
                    triggerClassName
                ].filter(Boolean).join(' ')}
                style={{
                    width: '100%',
                    ...(buttonWidthCh ? { maxWidth:`${buttonWidthCh}ch` } : {})
                }}
                onClick={() => !disabled && setOpen(v => !v)}
                onKeyDown={onTriggerKeyDown}
                aria-expanded={open}
                aria-haspopup="listbox"
                disabled={disabled}
            >
                <span className="flex-grow-1 text-truncate text-start min-w-0 text-muted">
                    {selected ?? placeholder}
                </span>
                <ChevronDown />
            </button>

            {portal(
                <div
                    className="dropdown-menu show p-2 shadow enum-menu"
                    style={{
                        minWidth: '100%',
                        maxHeight: '280px',
                        ...(buttonWidthCh ? { maxWidth:`${buttonWidthCh}ch` } : {})
                    }}
                    role="listbox"
                    aria-label="Choose option"
                    onMouseDown={(e) => {
                        // Allow clicking on the input field, but prevent default for other elements
                        // to keep focus in the input when clicking scrollbar or other menu areas
                        if (e.target !== inputRef.current) {
                            e.preventDefault();
                        }
                    }}
                >
                    <input
                        ref={inputRef}
                        type="text"
                        className="form-control mb-2"
                        placeholder={inputPlaceholder}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onInputKeyDown}
                        aria-autocomplete="list"
                    />

                    <div ref={listRef} style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {clearable && (
                            <>
                                <button
                                    type="button"
                                    className="dropdown-item text-muted"
                                    onClick={clear}
                                >
                                    {placeholder}
                                </button>
                                <div className="dropdown-divider" />
                            </>
                        )}

                        {filtered.length === 0 ? (
                            <div className="dropdown-item text-muted" aria-disabled="true">
                                {noResultsText}
                            </div>
                        ) : (
                            filtered.map((v, idx) => {
                                const active = selected !== undefined && v === selected;
                                const disabledOpt = isDisabled(v);
                                return (
                                    <button
                                        key={`${v}-${idx}`}
                                        type="button"
                                        data-idx={idx}
                                        className={
                                            'dropdown-item' +
                                            (active ? ' active' : '') +
                                            (disabledOpt ? ' disabled text-muted' : '')
                                        }
                                        onClick={() => !disabledOpt && choose(v)}
                                        disabled={disabledOpt}
                                        aria-disabled={disabledOpt}
                                    >
                                        {v}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const SearchableStringListDropdownBase = forwardRef(SearchableStringListDropdownInner);
SearchableStringListDropdownBase.displayName = 'SearchableStringListDropdown';

export const SearchableStringListDropdown = SearchableStringListDropdownBase;
export default SearchableStringListDropdown;
